/* Dico transparent header - homepage only.
   The header sits over the hero and goes solid white while a navigation panel is
   open. The announcement bar above it stays opaque and is not overlapped.

   ==================================================================
   The mobile flicker, and the two things that caused it

   Symptom: scrolling the homepage on a phone, the header would lose its
   transparency for a few frames -- a white flash, the announcement bar sliding
   into view, the page appearing to jump -- then settle back. Invisible on a
   desktop.

   Both causes come down to the same thing: #mainContent is pulled up by
   --dicoHeaderH, and #mainContent sits at the top of the document. Anything that
   changes that offset moves every pixel below it. Doing that mid-scroll is what
   the phone was showing.

   (1) The measurement used to take the class off, read the layout, and put it
       back. For that moment the offset was gone entirely: the page dropped by the
       header's height and the header painted solid white. It was wired to resize,
       and iOS Safari and Chrome both fire resize every time the address bar
       collapses or expands -- roughly every change of scroll direction. Hence a
       flicker on every flick of the thumb, and none on a desktop, where resize
       only fires when you drag the window.

       It was also unnecessary. --dicoHeaderH is the header's OWN height, and that
       does not depend on the class; the class only moves the content underneath.
       So the reading is taken with the class left on, and once set it stays set.

   (2) Even with that fixed, publishing a NEW height mid-scroll does the same
       damage on a smaller scale. The first paint uses a cached or default height,
       and the real one only becomes knowable once the header, its font and the
       logo image have loaded -- events that land well after a fast visitor has
       started scrolling. So a corrected value is held back until the visitor is
       at the top of the page, where moving the content costs nothing. It is
       written to sessionStorage immediately either way, so the next page view
       starts from the right number and never needs correcting at all.

   Nothing added since is allowed to break either rule: the dark-marks feature
   below changes colours only, never the class that sets the offset and never
   --dicoHeaderH.
   ================================================================== */
(function () {
    'use strict';

    var ROOT = document.documentElement;
    var ON = 'dico-th';
    var OPEN = 'dico-th-menu-open';
    var DARK = 'dico-th-dark';
    var KEY = 'dicoHeaderH';
    var lastOpen = null;
    var lastDark = null;
    var lastWidth = window.innerWidth;
    var lastHeight = window.innerHeight;
    var deferred = null;

    var config = window.dicoTransparentHeader || {};
    /* The URL switch exists so the look can be checked on a phone and a desktop
       from one link, without changing a theme setting. */
    var darkWanted = config.darkOverContent === true ||
        /[?&]dico-dark=1/.test(window.location.search);

    /* Applied at script execution rather than at DOMContentLoaded. This file is
       loaded from the header section group, so it runs before the page content is
       parsed -- early enough that the header is transparent on the very first
       paint. Waiting for DOMContentLoaded meant every page load began with a white
       header that then flipped, which on a slow connection was its own flash. */
    function armEarly() {
        try {
            var cached = window.sessionStorage.getItem(KEY);
            if (cached) { ROOT.style.setProperty('--dicoHeaderH', cached); }
        } catch (e) { /* private mode: fall back to the CSS default */ }
        ROOT.classList.add(ON);
    }

    function headerEl() {
        return document.getElementById('mainHeader');
    }

    function scrollY() {
        return window.pageYOffset || ROOT.scrollTop || 0;
    }

    function scrolled() {
        return scrollY() > 1;
    }

    /* A panel counts as open only when it actually renders a box.
       aria-expanded is unreliable here: this theme leaves it set to
       "true" on the summary after the megamenu has closed again. */
    function anyMenuOpen() {
        var panels = document.querySelectorAll('[id^="megamenu-shop"], .megamenu-wrapper, .women-megamenu-wrapper, .men-megamenu-wrapper');
        var i;
        for (i = 0; i < panels.length; i++) {
            if (panels[i].getBoundingClientRect().height > 0) { return true; }
        }
        var opened = document.querySelectorAll('#mainHeader details[open], header details[open], .drawer[open], dialog[open]');
        for (i = 0; i < opened.length; i++) {
            if (opened[i].getBoundingClientRect().height > 0) { return true; }
        }
        return false;
    }

    function sync() {
        var isOpen = anyMenuOpen();
        if (isOpen === lastOpen) { return; }
        lastOpen = isOpen;
        if (isOpen) {
            ROOT.classList.add(OPEN);
        } else {
            ROOT.classList.remove(OPEN);
        }
    }

    /* ---------------------------------------------------------------
       White marks over imagery, black marks over everything else.

       Decided by what is behind the header, not by scroll position. The homepage
       alternates hero photo, product row, hero photo, product row all the way
       down, so "past the hero" is not a thing that happens once -- the header
       crosses back onto imagery again and again.

       An IntersectionObserver does the work rather than a scroll handler: the
       root is shrunk from the bottom until only the strip the header occupies is
       left, and any hero image entering that strip flips the marks white. No
       per-frame measuring, and the browser stops calling us entirely while
       nothing is crossing the boundary.

       ---- Why nearTop() exists ----

       That strip is measured from the top of the VIEWPORT, but the header is only
       flush with the top of the viewport once the announcement bar has scrolled
       away. Before that it sits below the bar -- and on a phone the bar wraps to
       two lines, so the header starts around 50px down, entirely below the strip.
       Nothing registered as being behind it, and "nothing behind it" means black:
       opening the site fresh on an iPhone gave a black header on a photograph.

       Rather than chase the header with a margin that would have to be rebuilt on
       every pixel of those first 50px, the first 50px are simply answered
       directly. While the page is scrolled less than the bar's height, the header
       is over the top of #mainContent by definition, and the top of #mainContent
       is the hero. So: white, no measuring required. Past that the header is
       stuck at zero and the strip is exact.

       Default is black. If the homepage has no hero media at all the feature
       switches itself off instead, so a layout this was not written for cannot
       end up with black marks sitting on a dark photograph.
       --------------------------------------------------------------- */
    var io = null;
    var overCount = 0;
    var observing = false;
    var stickyFrom = 0;

    /* How far the page must scroll before the header is flush with the top of the
       viewport. Equal to the announcement bar's height, or 0 once it is dismissed. */
    function measureStickyFrom() {
        var h = headerEl();
        if (!h) { return; }
        var top = h.getBoundingClientRect().top + scrollY();
        stickyFrom = top > 0 ? Math.round(top) : 0;
    }

    function nearTop() {
        return scrollY() <= stickyFrom + 2;
    }

    function setDark(on) {
        if (on === lastDark) { return; }
        lastDark = on;
        if (on) {
            ROOT.classList.add(DARK);
        } else {
            ROOT.classList.remove(DARK);
        }
    }

    function applyDark() {
        setDark(observing && overCount === 0 && !nearTop());
    }

    function onCross(entries) {
        for (var i = 0; i < entries.length; i++) {
            var e = entries[i];
            var was = e.target.dicoOverHeader === true;
            if (was === e.isIntersecting) { continue; }
            e.target.dicoOverHeader = e.isIntersecting;
            overCount += e.isIntersecting ? 1 : -1;
        }
        if (overCount < 0) { overCount = 0; }
        applyDark();
    }

    function buildObserver() {
        if (!darkWanted || !window.IntersectionObserver) { return; }

        var h = headerEl();
        var main = document.getElementById('mainContent');
        if (!h || !main) { return; }

        var media = main.querySelectorAll('.hs-media, .gs-media, [data-dico-header-light]');
        if (!media.length) {
            observing = false;
            applyDark();
            return;
        }

        if (io) { io.disconnect(); }
        overCount = 0;

        var band = Math.ceil(h.getBoundingClientRect().height) || 45;
        var cut = Math.max(0, window.innerHeight - band);

        io = new IntersectionObserver(onCross, {
            rootMargin: '0px 0px -' + cut + 'px 0px',
            threshold: 0
        });

        for (var i = 0; i < media.length; i++) {
            media[i].dicoOverHeader = false;
            io.observe(media[i]);
        }

        observing = true;
        applyDark();
    }

    /* ------------------------------------------------------------------ */

    /* Publish a height. Held back while the visitor is scrolled, because this
       value moves #mainContent and therefore the whole page under them. */
    function publish(value) {
        if (ROOT.style.getPropertyValue('--dicoHeaderH') === value) {
            deferred = null;
            return;
        }
        if (scrolled()) { deferred = value; return; }
        ROOT.style.setProperty('--dicoHeaderH', value);
        deferred = null;
    }

    function flush() {
        if (deferred !== null && !scrolled()) { publish(deferred); }
    }

    function measure() {
        var h = headerEl();
        if (!h) { return; }
        var height = Math.round(h.getBoundingClientRect().height);
        if (height <= 0) { return; }

        var value = height + 'px';
        /* Saved unconditionally: even if it cannot be applied now, the next page
           view should start from it rather than repeat the correction. */
        try { window.sessionStorage.setItem(KEY, value); } catch (e) {}
        publish(value);
    }

    function remeasureAll() {
        measure();
        if (!scrolled()) { measureStickyFrom(); }
        buildObserver();
    }

    function boot() {
        measure();
        measureStickyFrom();
        sync();
        buildObserver();

        var evs = ['mouseover', 'mouseout', 'focusin', 'focusout', 'click', 'toggle', 'keyup'];
        for (var i = 0; i < evs.length; i++) {
            document.addEventListener(evs[i], function () {
                sync();
                window.setTimeout(sync, 60);
                window.setTimeout(sync, 260);
            }, true);
        }

        if (window.MutationObserver) {
            var mo = new MutationObserver(sync);
            var h = headerEl();
            if (h) {
                mo.observe(h, { subtree: true, attributes: true, attributeFilter: ['open', 'class', 'style', 'aria-expanded', 'hidden'] });
            }
        }

        /* One timer for all three jobs. flush() and applyDark() are comparisons
           that do nothing unless something is actually waiting, so they cost
           nothing to run here -- and this is what guarantees a held-back height
           eventually lands, rather than relying on a scroll event happening to
           fire at 0 (smooth scrolling can finish without one). */
        window.setInterval(function () { sync(); flush(); applyDark(); }, 200);

        window.addEventListener('scroll', function () {
            flush();
            applyDark();
        }, { passive: true });

        /* A resize where only the height moved is the mobile address bar, not a
           layout change: the header is exactly as tall as it was, so re-measuring
           it is pointless work during a scroll. The observer's band is measured
           from the top of the viewport though, so a height change does move its
           lower edge and it is rebuilt either way. Rebuilding touches no classes
           and no custom properties, so it cannot cause the shift above. */
        var rt, ot;
        window.addEventListener('resize', function () {
            if (window.innerHeight !== lastHeight) {
                lastHeight = window.innerHeight;
                window.clearTimeout(ot);
                ot = window.setTimeout(buildObserver, 200);
            }
            if (window.innerWidth === lastWidth) { return; }
            lastWidth = window.innerWidth;
            window.clearTimeout(rt);
            rt = window.setTimeout(remeasureAll, 150);
        });

        window.addEventListener('orientationchange', function () {
            window.setTimeout(remeasureAll, 300);
        });

        /* The logo is an image, so the header can grow a few pixels after its font
           and artwork land -- and the announcement bar can change line count with
           them, which moves stickyFrom. These are the readings most likely to
           arrive after the visitor has begun scrolling, which is why publish()
           defers. */
        window.addEventListener('load', remeasureAll);
        if (document.fonts && document.fonts.ready) {
            document.fonts.ready.then(remeasureAll);
        }

        document.addEventListener('dico:announcement-dismissed', function () {
            measure();
            measureStickyFrom();
            applyDark();
        });
        document.addEventListener('shopify:section:load', remeasureAll);
    }

    armEarly();

    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', boot);
    } else {
        boot();
    }
}());
